/**
 * Resumo legível de um user agent para a lista de sessões (Administração → Usuários → Atividade):
 * "Chrome · Windows", "Safari · iOS", "Edge · Android"… Função pura, testada em userAgent.test.ts.
 *
 * A ordem dos testes importa: Edge e Opera se anunciam como Chrome, Chrome se anuncia como Safari,
 * e iPad/iPhone trazem "Mac OS X" no texto.
 */

export interface UserAgentSummary {
  browser: string | null;
  os: string | null;
}

const BROWSERS: [RegExp, string][] = [
  [/\bEdg(?:e|A|iOS)?\//i, 'Edge'],
  [/\bOPR\/|\bOpera\b/i, 'Opera'],
  [/\bSamsungBrowser\//i, 'Samsung Internet'],
  [/\bFirefox\/|\bFxiOS\//i, 'Firefox'],
  [/\bCriOS\//i, 'Chrome'],
  [/\bChrome\//i, 'Chrome'],
  [/\bSafari\//i, 'Safari'],
];

const SYSTEMS: [RegExp, string][] = [
  [/\b(?:iPhone|iPad|iPod)\b/i, 'iOS'],
  [/\bAndroid\b/i, 'Android'],
  [/\bWindows\b/i, 'Windows'],
  [/\bMac OS X\b|\bMacintosh\b/i, 'macOS'],
  [/\bCrOS\b/i, 'ChromeOS'],
  [/\bLinux\b/i, 'Linux'],
];

/** Navegador e sistema (ou `null` quando não reconhece). */
export const parseUserAgent = (ua?: string | null): UserAgentSummary => {
  const text = (ua ?? '').trim();
  if (!text) return { browser: null, os: null };
  const browser = BROWSERS.find(([re]) => re.test(text))?.[1] ?? null;
  const os = SYSTEMS.find(([re]) => re.test(text))?.[1] ?? null;
  return { browser, os };
};

/** "Chrome · Windows"; só uma das partes quando a outra não é reconhecida; "Desconhecido" sem nada. */
export const summarizeUserAgent = (ua?: string | null): string => {
  const { browser, os } = parseUserAgent(ua);
  const parts = [browser, os].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(' · ') : 'Desconhecido';
};
